import React, { useState, useEffect, useCallback } from 'react';
import MessageThread from '../components/MessageThread';
import LoadingLeaf from '../components/LoadingLeaf';
import { getConversations, getClients, sendClientInvite } from '../api/client';

const initialsOf = (name) => (name || '').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

const formatWhen = (iso) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) return d.toLocaleTimeString('default', { hour: 'numeric', minute: '2-digit' });
  return d.toLocaleDateString('default', { month: 'short', day: 'numeric' });
};

export default function Messages() {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allClients, setAllClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedPortalSetUp, setSelectedPortalSetUp] = useState(true);
  const [showCompose, setShowCompose] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteMessage, setInviteMessage] = useState('');

  const fetchConversations = useCallback(async () => {
    try {
      const res = await getConversations();
      setConversations(res.data || []);
    } catch (err) {
      setError('Could not load conversations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    getClients().then(res => setAllClients(res.data || [])).catch(() => {});
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const selectConversation = (conv) => {
    setSelectedClientId(conv.client_id);
    setSelectedClient({ id: conv.client_id, name: conv.client_name });
    setSelectedPortalSetUp(conv.portal_set_up);
    setInviteMessage('');
    setShowCompose(false);
  };

  const selectNewClient = (c) => {
    setSelectedClientId(c.id);
    setSelectedClient({ id: c.id, name: c.name });
    setSelectedPortalSetUp(!!c.portal_password_hash);
    setInviteMessage('');
    setShowCompose(false);
  };

  const handleMessageSent = () => {
    fetchConversations();
  };

  const handleResendInvite = async () => {
    setSendingInvite(true);
    setInviteMessage('');
    try {
      await sendClientInvite(selectedClientId);
      setInviteMessage('Invite sent.');
    } catch (err) {
      setInviteMessage(err.response?.data?.detail || 'Could not send invite.');
    } finally {
      setSendingInvite(false);
    }
  };

  const filteredComposeClients = allClients.filter(c =>
    c.name.toLowerCase().includes(composeSearch.toLowerCase())
  );

  return (
    <div style={styles.page}>
      <div style={styles.list}>
        <div style={styles.listHeader}>
          <div style={styles.listTitle}>Messages</div>
          <button style={styles.composeBtn} onClick={() => { setShowCompose(s => !s); setComposeSearch(''); }}>
            {showCompose ? '×' : '+ New'}
          </button>
        </div>

        {showCompose && (
          <div style={styles.composePanel}>
            <input
              style={styles.composeSearch}
              placeholder="Search clients..."
              value={composeSearch}
              onChange={e => setComposeSearch(e.target.value)}
              autoFocus
            />
            <div style={styles.composeList}>
              {filteredComposeClients.length === 0 ? (
                <div style={styles.composeEmpty}>No clients found.</div>
              ) : (
                filteredComposeClients.map(c => (
                  <div key={c.id} style={styles.composeRow} onClick={() => selectNewClient(c)}>
                    <div style={styles.avatar}>{initialsOf(c.name)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={styles.composeRowName}>{c.name}</div>
                      {!c.portal_password_hash && <div style={styles.notSetUpTag}>Portal not set up</div>}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {error && <div style={styles.errorAlert}>{error}</div>}

        <div style={styles.listScroll}>
          {loading ? (
            <div style={styles.emptyState}><LoadingLeaf size={44} /></div>
          ) : conversations.length === 0 ? (
            <div style={styles.emptyState}>
              No conversations yet. Click "+ New" to message a client.
            </div>
          ) : (
            conversations.map(conv => (
              <div
                key={conv.client_id}
                style={{ ...styles.convRow, ...(selectedClientId === conv.client_id ? styles.convRowActive : {}) }}
                onClick={() => selectConversation(conv)}
              >
                <div style={styles.avatar}>{initialsOf(conv.client_name)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={styles.convRowTop}>
                    <span style={styles.convName}>{conv.client_name}</span>
                    <span style={styles.convTime}>{formatWhen(conv.last_message_at)}</span>
                  </div>
                  <div style={styles.convRowBottom}>
                    <span style={styles.convPreview}>
                      {conv.last_sender_type === 'coach' ? 'You: ' : ''}{conv.last_message_preview}
                    </span>
                    {conv.unread_count > 0 && <span style={styles.unreadDot}>{conv.unread_count}</span>}
                  </div>
                  {!conv.portal_set_up && <div style={styles.notSetUpTag}>Portal not set up</div>}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div style={styles.threadPane}>
        {!selectedClientId ? (
          <div style={styles.emptyThread}>
            <div style={{ fontSize: '36px', marginBottom: '14px' }}>💬</div>
            <p>Select a conversation, or start a new one.</p>
          </div>
        ) : (
          <>
            {!selectedPortalSetUp && (
              <div style={styles.portalBanner}>
                <span>{selectedClient?.name} hasn't set up their portal yet.</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {inviteMessage && <span style={styles.inviteMsg}>{inviteMessage}</span>}
                  <button style={styles.resendBtn} onClick={handleResendInvite} disabled={sendingInvite}>
                    {sendingInvite ? 'Sending...' : 'Resend invite'}
                  </button>
                </div>
              </div>
            )}
            <div style={styles.threadScroll}>
              <MessageThread clientId={selectedClientId} client={selectedClient} onMessageSent={handleMessageSent} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { display: 'flex', height: '100%', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" },
  list: { width: '320px', flexShrink: 0, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', background: 'white' },
  listHeader: { padding: '20px 20px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  listTitle: { fontFamily: "'Lora', Georgia, serif", fontSize: '22px', fontWeight: '400', color: 'var(--text-dark)' },
  composeBtn: { padding: '7px 14px', borderRadius: '20px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  composePanel: { borderBottom: '1px solid var(--border-light)', padding: '0 12px 12px' },
  composeSearch: { width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--cream)', fontFamily: "'DM Sans', sans-serif", fontSize: '13.5px', outline: 'none', boxSizing: 'border-box', marginBottom: '8px' },
  composeList: { maxHeight: '260px', overflowY: 'auto' },
  composeEmpty: { padding: '16px', fontSize: '13px', color: 'var(--text-soft)', textAlign: 'center' },
  composeRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 6px', borderRadius: '8px', cursor: 'pointer' },
  composeRowName: { fontSize: '13.5px', color: 'var(--text-dark)', fontWeight: '500' },
  listScroll: { flex: 1, overflowY: 'auto' },
  emptyState: { padding: '30px 20px', fontSize: '13.5px', color: 'var(--text-soft)', textAlign: 'center' },
  errorAlert: { margin: '0 12px 12px', background: 'var(--red-light)', color: 'var(--red)', padding: '10px 12px', borderRadius: '8px', fontSize: '13px' },
  convRow: { display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 20px', cursor: 'pointer', borderLeft: '3px solid transparent' },
  convRowActive: { background: 'var(--mint)', borderLeft: '3px solid var(--sage)' },
  avatar: { width: '38px', height: '38px', borderRadius: '50%', background: 'var(--mint)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', color: 'var(--sage)', flexShrink: 0 },
  convRowTop: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px' },
  convName: { fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  convTime: { fontSize: '11px', color: 'var(--text-faint)', flexShrink: 0 },
  convRowBottom: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginTop: '2px' },
  convPreview: { fontSize: '12.5px', color: 'var(--text-soft)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 },
  unreadDot: { background: 'var(--sage)', color: 'white', borderRadius: '20px', fontSize: '10.5px', fontWeight: '700', padding: '1px 7px', flexShrink: 0 },
  notSetUpTag: { fontSize: '10.5px', color: 'var(--gold)', fontWeight: '600', marginTop: '3px' },
  threadPane: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '20px 24px' },
  threadScroll: { flex: 1, overflowY: 'auto' },
  emptyThread: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-soft)', fontSize: '14px' },
  portalBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', background: 'var(--gold-pale)', border: '1px solid rgba(196,149,106,0.3)', borderRadius: '10px', padding: '10px 16px', marginBottom: '14px', fontSize: '13px', color: '#7a5a18' },
  resendBtn: { padding: '7px 14px', borderRadius: '8px', background: 'var(--sage)', border: 'none', color: 'white', fontFamily: "'DM Sans', sans-serif", fontSize: '12.5px', fontWeight: '500', cursor: 'pointer', whiteSpace: 'nowrap' },
  inviteMsg: { fontSize: '12px', color: '#7a5a18' },
};
